import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createAnnotator, type AnnotateMode, type Annotator } from '@civil/annotate';
import {
  DRAW_TOOLS,
  type DrawTool,
  type InterpretationItem,
  type MarkGeometry,
  type MarkMatch,
  type RouteMatch,
  type UiMark,
  type UiRequest,
  type UiSnapshot,
} from '@civil/schema';
import { blobToDataUrl, drawnSnapshot } from '../ui-render.js';
import { canRunExtension, helloExtension, linkExtension, openWithExtension, pairExtension, type ExtensionLink } from '../extension.js';
import type { ExtensionHello, FromExtension } from '@civil/schema';
import { zip } from '../zip.js';
import { isAbortError } from '../project.js';
import {
  addMark,
  addSnapshot,
  applyUiOps,
  createRequest,
  deleteMark,
  deletePersonalLink,
  deleteRequest,
  deleteSnapshot,
  fetchRequest,
  fetchUiState,
  isTouchDevice,
  matchRequest,
  interpretRequest,
  correctInterpretation,
  exportRequest,
  readAsDataUrl,
  renameRequest,
  setPersonalLink,
  updateMark,
  type UiState,
} from '../ui.js';

/**
 * The UI tab (docs/ui-tab.md): pick a frontend and where it runs, gather snapshots of
 * it into a request, and mark them up — Annotate pins notes to places, Draw sketches.
 * Every mark is kept by the API the moment it is made; this pane holds nothing the
 * server does not.
 *
 * Marking up in place is the Chrome extension's (§9.2): its overlay sends what it makes
 * here, as it is made. Without it, and always on a phone, a snapshot is a screenshot
 * pasted, dropped or chosen here.
 */

export interface Frontend {
  id: string;
  name: string;
}

/** What App's command context needs to know (CLAUDE.md: commands are offered only where they apply). */
export interface UiTabState {
  hasRequest: boolean;
  hasSnapshot: boolean;
  hasSelectedMark: boolean;
  mode: AnnotateMode;
}

/** What the named commands do here (commands/registry.ts), each answering in words. */
export interface UiTabHandle {
  newRequest(): Promise<string | undefined>;
  capture(): Promise<string | undefined>;
  match(): Promise<string | undefined>;
  interpret(): Promise<string | undefined>;
  copyPrompt(): Promise<string | undefined>;
  download(): Promise<string | undefined>;
  chooseScreenshot(): string | undefined;
  setMode(mode: AnnotateMode): string;
  removeSelectedMark(): Promise<string | undefined>;
}

const TOOL_NAMES: Record<DrawTool, string> = { pen: 'Pen', arrow: 'Arrow', box: 'Box', text: 'Text' };
const MODE_NAMES: Record<AnnotateMode, string> = { view: 'View', annotate: 'Annotate', draw: 'Draw' };

const describeMark = (g: MarkGeometry): string =>
  g.kind === 'pin' ? 'Note' : g.kind === 'region' ? 'Region' : g.tool === 'text' ? 'Text' : TOOL_NAMES[g.tool];

export const UiTab = forwardRef<UiTabHandle, {
  projectId: string;
  frontends: Frontend[];
  /** civil/ui.yaml's links, by frontend (from the bundle, pending edits included). */
  sharedLinks: Record<string, Record<string, string>>;
  linksError?: string | undefined;
  onEffect: (title: string, detail: string, failed?: boolean) => void;
  /** A shared link was written as a pending change: the bundle should be read again. */
  onSharedLinksChanged: () => void;
  onState: (state: UiTabState) => void;
  /** Opens a file at a line in the code view: where a match's evidence is. */
  onOpenFile: (path: string, line?: number) => void;
  /** Who is signed in: the account the extension is connected as. */
  me: { email: string; name?: string | null };
}>(function UiTab({ projectId, frontends, sharedLinks, linksError, onEffect, onSharedLinksChanged, onState, onOpenFile, me }, ref) {
  const [state, setState] = useState<UiState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [frontend, setFrontend] = useState<string>(() => frontends[0]?.id ?? 'app');
  const [linkName, setLinkName] = useState<string>('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [request, setRequest] = useState<UiRequest | null>(null);
  const [snapshotId, setSnapshotId] = useState<string | null>(null);
  const [mode, setMode] = useState<AnnotateMode>('annotate');
  const [tool, setTool] = useState<DrawTool>('pen');
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [matching, setMatching] = useState(false);
  const [interpreting, setInterpreting] = useState(false);
  /**
   * Civil's Chrome extension (docs/ui-tab.md §9.2): `undefined` while asking, null when it
   * is not installed, else what it said — connected, and by whom, or not yet.
   */
  const [extension, setExtension] = useState<ExtensionHello | null | undefined>(undefined);
  const [opened, setOpened] = useState<string | null>(null);
  /** Open the application in a window of a phone's size: its responsive layout, from a desktop. */
  const [phoneSize, setPhoneSize] = useState(false);
  const [addingLink, setAddingLink] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const touch = useMemo(isTouchDevice, []);

  // Frontends come from the composition; with none, the application is one.
  const choices: Frontend[] = frontends.length ? frontends : [{ id: 'app', name: 'The application' }];
  useEffect(() => {
    if (!choices.some((f) => f.id === frontend)) setFrontend(choices[0]!.id);
  }, [choices, frontend]);

  const refresh = useCallback(
    (signal?: AbortSignal) =>
      fetchUiState(projectId, signal)
        .then((s) => {
          setState(s);
          setLoadError(null);
          return s;
        })
        .catch((error: unknown) => {
          if (!isAbortError(error)) setLoadError((error as Error).message);
          return null;
        }),
    [projectId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal).then((s) => {
      // Open the latest request, so coming back to the tab finds the work where it was.
      if (s && !requestId && s.requests[0]) setRequestId(s.requests[0].id);
    });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const loadRequest = useCallback(
    async (id: string, signal?: AbortSignal) => {
      try {
        const r = await fetchRequest(projectId, id, signal);
        setRequest(r);
        setFrontend(r.frontend);
        setSnapshotId((current) => (current && r.snapshotList.some((s) => s.id === current) ? current : (r.snapshotList.at(-1)?.id ?? null)));
      } catch (error) {
        if (!isAbortError(error)) onEffect('Request', (error as Error).message, true);
      }
    },
    [projectId, onEffect],
  );

  useEffect(() => {
    if (!requestId) {
      setRequest(null);
      return;
    }
    const controller = new AbortController();
    void loadRequest(requestId, controller.signal);
    return () => controller.abort();
  }, [requestId, loadRequest]);

  // Links for the chosen frontend: the shared ones first, then the engineer's own.
  const links = useMemo(() => {
    const shared = Object.entries(sharedLinks[frontend] ?? {}).map(([name, url]) => ({ name, url, shared: true }));
    const mine = (state?.personalLinks ?? []).filter((l) => l.frontend === frontend).map((l) => ({ name: l.name, url: l.url, shared: false }));
    return [...shared, ...mine.filter((m) => !shared.some((s) => s.name === m.name))];
  }, [sharedLinks, state?.personalLinks, frontend]);
  const link = links.find((l) => l.name === linkName) ?? links[0];
  useEffect(() => {
    if (link) setPageUrl((current) => (current && sameOrigin(current, link.url) ? current : link.url));
  }, [link]);

  const snapshot = request?.snapshotList.find((s) => s.id === snapshotId) ?? null;
  const selectedMark = snapshot?.marks.find((m) => m.id === selected) ?? null;

  useEffect(() => {
    onState({ hasRequest: !!request, hasSnapshot: !!snapshot, hasSelectedMark: !!selectedMark, mode });
  }, [onState, request, snapshot, selectedMark, mode]);

  const act = async <T,>(title: string, work: () => Promise<T & { effect: string }>, quiet = false): Promise<(T & { effect: string }) | undefined> => {
    setBusy(true);
    try {
      const result = await work();
      if (!quiet) onEffect(title, result.effect);
      return result;
    } catch (error) {
      onEffect(title, (error as Error).message, true);
      return undefined;
    } finally {
      setBusy(false);
    }
  };

  const newRequest = async (): Promise<string | undefined> => {
    const made = await act('New request', () => createRequest(projectId, frontend));
    if (!made) return undefined;
    setRequestId(made.request.id);
    setRequest(made.request);
    setSnapshotId(null);
    void refresh();
    return made.effect;
  };

  /** A screenshot, from a file, the clipboard or a drop. Starts a request when there is none. */
  const upload = async (file: Blob) => {
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      onEffect('Add a screenshot', 'A screenshot must be a PNG, JPEG or WebP picture.', true);
      return;
    }
    const url = pageUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      onEffect('Add a screenshot', 'Say which page it is first: its address, starting http:// or https://.', true);
      return;
    }
    let target = request;
    if (!target) {
      const made = await act('New request', () => createRequest(projectId, frontend), true);
      if (!made) return;
      target = made.request;
      setRequestId(target.id);
    }
    const image = await readAsDataUrl(file);
    const added = await act('Add a screenshot', () => addSnapshot(projectId, target.id, { image, url }));
    if (!added) return;
    setSnapshotId(added.snapshot.id);
    setMode('annotate');
    await loadRequest(target.id);
    void refresh();
  };

  // Pasting a screenshot anywhere on the tab adds it.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      const file = [...(event.clipboardData?.files ?? [])].find((f) => f.type.startsWith('image/'));
      if (!file) return;
      event.preventDefault();
      void upload(file);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  });

  const askExtension = useCallback(() => {
    if (touch || !canRunExtension()) {
      setExtension(null);
      return;
    }
    void helloExtension().then(setExtension);
  }, [touch]);
  useEffect(() => askExtension(), [askExtension]);

  /**
   * What the extension's overlay makes, saved here as it is made (docs/ui-tab.md §9.2):
   * each snapshot, mark, note and removal through Civil's own API, the saved id going
   * back to the extension. Another project's items are another Civil tab's to save.
   */
  const fromExtension = async (m: FromExtension): Promise<{ saved?: string; requestId?: string } | null> => {
    if (m.target.projectId !== projectId) return null;
    if (m.type === 'snapshot') {
      let requestId = m.target.requestId ?? request?.id;
      if (!requestId) {
        const made = await createRequest(projectId, m.target.frontend ?? frontend);
        requestId = made.request.id;
      }
      const added = await addSnapshot(projectId, requestId, { image: m.image, url: m.url, viewport: m.viewport, capturedBy: 'extension', elements: m.elements });
      onEffect('Mark up', `${added.effect} From the overlay, with ${m.elements.length} things on screen read.`);
      setRequestId(requestId);
      setSnapshotId(added.snapshot.id);
      await loadRequest(requestId);
      void refresh();
      return { saved: added.snapshot.id, requestId };
    }
    if (m.type === 'mark') {
      const made = await addMark(projectId, m.snapshot, { geometry: m.geometry, note: m.note, ...(m.element !== undefined ? { element: m.element } : {}) });
      if (requestId) await loadRequest(requestId);
      void refresh();
      return { saved: made.mark.id };
    }
    if (m.type === 'mark-note') {
      await updateMark(projectId, m.mark, { note: m.note });
      if (requestId) await loadRequest(requestId);
      return {};
    }
    await deleteMark(projectId, m.mark);
    if (requestId) await loadRequest(requestId);
    void refresh();
    return {};
  };
  const latestFromExtension = useRef(fromExtension);
  latestFromExtension.current = fromExtension;
  const link_ = useRef<ExtensionLink | null>(null);
  const connected = !!extension?.account;
  useEffect(() => {
    if (!connected) return;
    const linked = linkExtension((m) => latestFromExtension.current(m));
    link_.current = linked;
    return () => {
      linked.close();
      link_.current = null;
    };
  }, [connected]);
  // Where this tab is, so captures from tabs not opened here come to it.
  useEffect(() => {
    link_.current?.here({ projectId, ...(request ? { requestId: request.id } : {}), frontend: request?.frontend ?? frontend });
  }, [connected, projectId, request, frontend]);

  /** Connects the extension as the signed-in engineer: no secret changes hands. */
  const connectExtension = async (): Promise<string | undefined> => {
    const answer = await pairExtension({ email: me.email, ...(me.name ? { name: me.name } : {}) });
    if (!answer?.ok) {
      onEffect('Extension', 'The extension did not answer: reload this page after installing it, and try again.', true);
      return undefined;
    }
    askExtension();
    const said = `Connected Civil’s extension as ${me.email}.${answer.popup ? '' : ' Its icon in the toolbar shows it.'}`;
    onEffect('Extension', said);
    return said;
  };

  /** Opens the application in a new tab with Civil's overlay on it; what is marked there comes here. */
  const openWithCivil = async (): Promise<string | undefined> => {
    const url = pageUrl.trim() || link?.url;
    if (!url || !/^https?:\/\//i.test(url)) {
      onEffect('Open with Civil', 'Say where the application runs first: pick a link, or give its address.', true);
      return undefined;
    }
    const target = { projectId, ...(request ? { requestId: request.id } : {}), frontend: request?.frontend ?? frontend };
    const answer = await openWithExtension(url, target, phoneSize);
    if (!answer?.ok) {
      onEffect('Open with Civil', `The extension could not open it${answer?.error ? `: ${answer.error}` : ''}.`, true);
      return undefined;
    }
    const said = answer.asking
      ? `Chrome is asking whether Civil may put its overlay on ${hostOf(url)}: answer in the window that opened, and the site opens with it.`
      : `Opened ${hostOf(url)}${phoneSize ? ' at a phone’s size' : ''} with Civil’s overlay. Press Mark up on it to hold the page still and mark it; what you mark arrives here as you go.`;
    setOpened(said);
    onEffect('Open with Civil', said);
    return said;
  };

  const createMark = async (made: { geometry: MarkGeometry; element?: number }) => {
    if (!snapshot || !request) return;
    const result = await act('Mark', () => addMark(projectId, snapshot.id, made), true);
    if (!result) return;
    setRequest({
      ...request,
      snapshotList: request.snapshotList.map((s) => (s.id === snapshot.id ? { ...s, marks: [...s.marks, result.mark] } : s)),
    });
    setSelected(result.mark.id);
    void refresh();
  };

  const saveMark = async (mark: UiMark, patch: { note?: string; geometry?: MarkGeometry }) => {
    if (!request) return;
    const result = await act('Note', () => updateMark(projectId, mark.id, patch), true);
    if (!result) return;
    setRequest({
      ...request,
      snapshotList: request.snapshotList.map((s) => ({
        ...s,
        marks: s.marks.map((m) => (m.id === mark.id ? { ...m, ...patch } : m)),
      })),
    });
  };

  const removeMark = async (mark: UiMark): Promise<string | undefined> => {
    if (!request) return undefined;
    const result = await act('Remove mark', () => deleteMark(projectId, mark.id), true);
    if (!result) return undefined;
    setRequest({ ...request, snapshotList: request.snapshotList.map((s) => ({ ...s, marks: s.marks.filter((m) => m.id !== mark.id) })) });
    if (selected === mark.id) setSelected(null);
    void refresh();
    return result.effect;
  };

  const match = async (): Promise<string | undefined> => {
    if (!request) return undefined;
    setMatching(true);
    try {
      const result = await matchRequest(projectId, request.id);
      setRequest(result.request);
      const said = [result.effect, ...result.notes].join(' ');
      onEffect('Match to code', said);
      return said;
    } catch (error) {
      onEffect('Match to code', (error as Error).message, true);
      return undefined;
    } finally {
      setMatching(false);
    }
  };

  /** Each snapshot with marks, drawn as the tab shows it, by id; one that cannot be drawn is left out and said. */
  const drawAll = async (r: UiRequest): Promise<{ drawn: Map<string, Blob>; failed: string[] }> => {
    const drawn = new Map<string, Blob>();
    const failed: string[] = [];
    for (const s of r.snapshotList) {
      try {
        drawn.set(s.id, await drawnSnapshot(s));
      } catch (error) {
        failed.push((error as Error).message);
      }
    }
    return { drawn, failed };
  };

  const interpret = async (): Promise<string | undefined> => {
    if (!request) return undefined;
    setInterpreting(true);
    try {
      const { drawn } = await drawAll({ ...request, snapshotList: request.snapshotList.filter((s) => s.marks.length) });
      const composites: Record<string, string> = {};
      for (const [id, blob] of drawn) composites[id] = await blobToDataUrl(blob);
      const result = await interpretRequest(projectId, request.id, composites);
      setRequest(result.request);
      void refresh();
      const said = [result.effect, ...result.notes].join(' ');
      onEffect('Interpret', said);
      return said;
    } catch (error) {
      onEffect('Interpret', (error as Error).message, true);
      return undefined;
    } finally {
      setInterpreting(false);
    }
  };

  const copyPrompt = async (): Promise<string | undefined> => {
    if (!request) return undefined;
    try {
      const { markdown } = await exportRequest(projectId, request.id);
      await navigator.clipboard.writeText(markdown);
      const said = 'Copied the request as a prompt. It names its images; download the folder to give an agent the pictures too.';
      onEffect('Copy prompt', said);
      return said;
    } catch (error) {
      onEffect('Copy prompt', (error as Error).message, true);
      return undefined;
    }
  };

  const download = async (): Promise<string | undefined> => {
    if (!request) return undefined;
    setBusy(true);
    try {
      const exported = await exportRequest(projectId, request.id);
      const { drawn, failed } = await drawAll(request);
      const files = [{ name: 'request.md', bytes: new TextEncoder().encode(exported.markdown) }];
      for (const image of exported.images) {
        const blob = drawn.get(image.snapshot);
        // A JPEG keeps the name the prompt gives it; image readers go by the bytes.
        if (blob) files.push({ name: image.name, bytes: new Uint8Array(await blob.arrayBuffer()) });
      }
      const name = `${(request.title || 'ui-request').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'ui-request'}.zip`;
      const url = URL.createObjectURL(zip(files));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      const said = `Downloaded ${name}: request.md and ${files.length - 1} image${files.length === 2 ? '' : 's'} with the marks drawn on.${failed.length ? ` Left out: ${failed.join('; ')}.` : ''}`;
      onEffect('Download', said, failed.length > 0);
      return said;
    } catch (error) {
      onEffect('Download', (error as Error).message, true);
      return undefined;
    } finally {
      setBusy(false);
    }
  };

  const correct = async (item: InterpretationItem) => {
    const interpretation = request?.interpretation;
    if (!request || !interpretation) return;
    const items = interpretation.items.map((i) => (i.snapshot === item.snapshot && i.mark === item.mark ? item : i));
    try {
      await correctInterpretation(projectId, request.id, interpretation.id, items);
      setRequest({ ...request, interpretation: { ...interpretation, items, correctedAt: new Date().toISOString() } });
    } catch (error) {
      onEffect('Correct', (error as Error).message, true);
    }
  };

  useImperativeHandle(ref, () => ({
    newRequest,
    capture: openWithCivil,
    match,
    interpret,
    copyPrompt,
    download,
    chooseScreenshot: () => {
      fileInput.current?.click();
      return 'Choose a screenshot to add.';
    },
    setMode: (next) => {
      setMode(next);
      return `${MODE_NAMES[next]}${next === 'annotate' ? ': tap to pin a note, drag to mark a region' : next === 'draw' ? `: drawing with the ${TOOL_NAMES[tool].toLowerCase()}` : ''}.`;
    },
    removeSelectedMark: async () => (selectedMark ? removeMark(selectedMark) : undefined),
  }));

  const openLink = link ? (pageUrl && sameOrigin(pageUrl, link.url) ? pageUrl : link.url) : undefined;

  return (
    <div className="ui-tab">
      {touch ? (
        <p className="ui-notice">
          Marking up an application in place needs Civil&rsquo;s Chrome extension on a desktop, or the Civil iOS app once it
          exists. Here you can add a screenshot and mark it up.
        </p>
      ) : null}
      {linksError ? <p className="gate-error">{linksError}</p> : null}
      {loadError ? <p className="gate-error">{loadError}</p> : null}
      {state?.imageStore === 'memory' ? (
        <p className="ui-notice muted">Development: snapshot images are kept in memory here, and go when the API restarts.</p>
      ) : null}

      <div className="ui-bar">
        <label className="ui-field">
          <span>Frontend</span>
          <select value={frontend} onChange={(e) => setFrontend(e.target.value)} disabled={!!request?.snapshotList.length}>
            {choices.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label className="ui-field">
          <span>Runs at</span>
          <select value={link?.name ?? ''} onChange={(e) => setLinkName(e.target.value)} disabled={!links.length}>
            {links.length ? null : <option value="">No links yet</option>}
            {links.map((l) => (
              <option key={l.name} value={l.name}>
                {l.name} — {hostOf(l.url)}
                {l.shared ? '' : ' (yours)'}
              </option>
            ))}
          </select>
        </label>
        {openLink ? (
          <a className="link ui-open" href={openLink} target="_blank" rel="noopener noreferrer">
            Open ↗
          </a>
        ) : null}
        <button type="button" className="link" onClick={() => setAddingLink((v) => !v)} aria-expanded={addingLink}>
          {addingLink ? 'Cancel' : 'Add a link'}
        </button>
        {link ? <LinkRemove projectId={projectId} frontend={frontend} link={link} onDone={(effect, failed) => {
          onEffect('Links', effect, failed);
          if (link.shared) onSharedLinksChanged();
          else void refresh();
        }} /> : null}
      </div>

      {addingLink ? (
        <LinkForm
          projectId={projectId}
          frontend={frontend}
          onDone={(effect, failed, shared, name) => {
            onEffect('Links', effect, failed);
            if (failed) return;
            setAddingLink(false);
            setLinkName(name);
            if (shared) onSharedLinksChanged();
            else void refresh();
          }}
        />
      ) : null}

      <div className="ui-body">
        <nav className="ui-requests" aria-label="Requests">
          <button type="button" className="connect" onClick={() => void newRequest()} disabled={busy}>
            New request
          </button>
          {state && !state.requests.length ? <p className="muted">No requests yet.</p> : null}
          <ul>
            {state?.requests.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className={r.id === requestId ? 'ui-request-item current' : 'ui-request-item'}
                  aria-current={r.id === requestId ? 'true' : undefined}
                  onClick={() => {
                    setRequestId(r.id);
                    setSelected(null);
                  }}
                >
                  <span>{r.title || 'Untitled request'}</span>
                  <span className="muted">
                    {r.frontend} · {r.snapshots} snapshot{r.snapshots === 1 ? '' : 's'} · {r.marks} mark{r.marks === 1 ? '' : 's'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <section className="ui-request" aria-label="Request">
          {!touch ? (
            <ExtensionBar
              extension={extension}
              me={me.email}
              opened={opened}
              canOpen={!!(pageUrl.trim() || link?.url)}
              target={pageUrl.trim() || link?.url || ''}
              phoneSize={phoneSize}
              onPhoneSize={setPhoneSize}
              onConnect={() => void connectExtension()}
              onOpen={() => void openWithCivil()}
              onCheck={askExtension}
            />
          ) : null}
          <AddScreenshot
            pageUrl={pageUrl}
            onPageUrl={setPageUrl}
            onFile={(file) => void upload(file)}
            inputRef={fileInput}
            busy={busy}
          />

          {request ? (
            <>
              <RequestHead
                key={request.id}
                request={request}
                matching={matching}
                interpreting={interpreting}
                onMatch={() => void match()}
                onInterpret={() => void interpret()}
                onCopy={() => void copyPrompt()}
                onDownload={() => void download()}
                onRename={async (title) => {
                  const done = await act('Rename', () => renameRequest(projectId, request.id, title), true);
                  if (done) {
                    setRequest({ ...request, title });
                    void refresh();
                  }
                }}
                onDelete={async () => {
                  if (!window.confirm(`Delete “${request.title || 'Untitled request'}” and its ${request.snapshotList.length} snapshot(s)? This cannot be undone.`)) return;
                  const done = await act('Delete request', () => deleteRequest(projectId, request.id));
                  if (!done) return;
                  setRequestId(null);
                  setRequest(null);
                  setSnapshotId(null);
                  void refresh();
                }}
              />
              {request.snapshotList.length ? (
                <ol className="ui-strip" aria-label="Snapshots">
                  {request.snapshotList.map((s, i) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        className={s.id === snapshotId ? 'ui-thumb current' : 'ui-thumb'}
                        aria-current={s.id === snapshotId ? 'true' : undefined}
                        onClick={() => {
                          setSnapshotId(s.id);
                          setSelected(null);
                        }}
                        title={s.url}
                      >
                        <img
                          src={s.image}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            // Expired with the bucket's lifecycle: the notes are still there.
                            e.currentTarget.style.visibility = 'hidden';
                            e.currentTarget.parentElement?.setAttribute('title', `${s.url} — image no longer kept`);
                          }}
                        />
                        <span>{i + 1}</span>
                      </button>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="muted ui-empty">Add a screenshot of {choices.find((f) => f.id === request.frontend)?.name ?? request.frontend} to mark it up.</p>
              )}
            </>
          ) : (
            <p className="muted ui-empty">
              A request gathers snapshots of the running application and what you mark on them. Add a screenshot to start one.
            </p>
          )}

          {snapshot && request ? (
            <SnapshotEditor
              key={snapshot.id}
              snapshot={snapshot}
              mode={mode}
              tool={tool}
              selected={selected}
              busy={busy}
              onMode={setMode}
              onTool={setTool}
              onSelect={setSelected}
              onCreate={(m) => void createMark(m)}
              onSave={(m, patch) => void saveMark(m, patch)}
              onRemove={(m) => void removeMark(m).then((effect) => effect && onEffect('Remove mark', effect))}
              onOpenFile={onOpenFile}
              interpretation={request.interpretation ?? null}
              snapshotNumber={request.snapshotList.findIndex((s) => s.id === snapshot.id) + 1}
              onCorrect={(item) => void correct(item)}
              onDeleteSnapshot={async () => {
                if (!window.confirm('Delete this snapshot and its marks?')) return;
                const done = await act('Delete snapshot', () => deleteSnapshot(projectId, snapshot.id));
                if (!done) return;
                setSnapshotId(null);
                await loadRequest(request.id);
                void refresh();
              }}
            />
          ) : null}
        </section>
      </div>
    </div>
  );
});

const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const sameOrigin = (a: string, b: string): boolean => {
  try {
    return new URL(a).origin === new URL(b).origin;
  } catch {
    return false;
  }
};

/**
 * Civil's extension, in the state it is in (docs/ui-tab.md §9.2): not installed — where to
 * get it; installed but not connected — connect it; connected — open the application with
 * the overlay on it.
 */
function ExtensionBar({
  extension,
  me,
  opened,
  canOpen,
  target,
  phoneSize,
  onPhoneSize,
  onConnect,
  onOpen,
  onCheck,
}: {
  extension: ExtensionHello | null | undefined;
  me: string;
  opened: string | null;
  canOpen: boolean;
  target: string;
  phoneSize: boolean;
  onPhoneSize: (on: boolean) => void;
  onConnect: () => void;
  onOpen: () => void;
  onCheck: () => void;
}) {
  if (extension === undefined) return null;
  if (!canRunExtension()) {
    return <p className="ui-ext muted">Marking up the application where it runs needs Civil’s extension, in Chrome, Edge or Brave.</p>;
  }
  if (extension === null) {
    return (
      <div className="ui-ext">
        <span className="ui-ext-state">Mark up the application where it runs, with notes and drawings right on the page.</span>
        <a className="connect ui-ext-go" href="/extension" target="_blank" rel="noopener">
          Get Civil’s extension ↗
        </a>
        <button type="button" className="link" onClick={onCheck}>
          Installed it? Check again
        </button>
      </div>
    );
  }
  if (!extension.account) {
    return (
      <div className="ui-ext">
        <span className="ui-ext-state">Civil’s extension is installed. Connect it to send what you mark here.</span>
        <button type="button" className="connect ui-ext-go" onClick={onConnect}>
          Connect the extension
        </button>
      </div>
    );
  }
  const other = extension.account.email.toLowerCase() !== me.toLowerCase();
  return (
    <div className="ui-ext">
      <button type="button" className="connect ui-ext-go" onClick={onOpen} disabled={!canOpen} title="Open it in a new tab with Civil’s overlay on it (K)">
        {canOpen ? `Open ${hostOf(target)} with Civil ↗` : 'Open with Civil'}
      </button>
      <label className="ui-phone-size" title="Its responsive layout at 390×844 — not touch, nor a phone’s browser">
        <input type="checkbox" checked={phoneSize} onChange={(e) => onPhoneSize(e.target.checked)} /> At a phone’s size
      </label>
      <span className="ui-ext-state muted">
        {other ? (
          <>
            Connected as {extension.account.email}.{' '}
            <button type="button" className="link" onClick={onConnect}>
              Connect as you
            </button>
          </>
        ) : (
          `Connected as ${extension.account.email}.`
        )}
      </span>
      {opened ? (
        <p className="ui-drop-note">
          {opened} The first time in that tab, click Civil’s icon or press <kbd>{extension.shortcut || 'Alt+Shift+M'}</kbd> so it can take the page’s pictures.
        </p>
      ) : !canOpen ? (
        <p className="ui-drop-note muted">Pick where it runs above, or give the page’s address, to open it with Civil.</p>
      ) : null}
    </div>
  );
}

function AddScreenshot({
  pageUrl,
  onPageUrl,
  onFile,
  inputRef,
  busy,
}: {
  pageUrl: string;
  onPageUrl: (url: string) => void;
  onFile: (file: File) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  busy: boolean;
}) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={over ? 'ui-drop over' : 'ui-drop'}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const file = [...e.dataTransfer.files].find((f) => f.type.startsWith('image/'));
        if (file) onFile(file);
      }}
    >
      <label className="ui-field ui-page">
        <span>Page</span>
        <input
          type="url"
          inputMode="url"
          placeholder="https://… the address of the page in the screenshot"
          value={pageUrl}
          onChange={(e) => onPageUrl(e.target.value)}
          aria-label="Address of the page in the screenshot"
        />
      </label>
      <button type="button" className="connect" onClick={() => inputRef.current?.click()} disabled={busy}>
        Add a screenshot
      </button>
      <span className="muted ui-drop-hint">or paste or drop one here</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
    </div>
  );
}

function RequestHead({
  request,
  matching,
  interpreting,
  onMatch,
  onInterpret,
  onCopy,
  onDownload,
  onRename,
  onDelete,
}: {
  request: UiRequest;
  matching: boolean;
  interpreting: boolean;
  onMatch: () => void;
  onInterpret: () => void;
  onCopy: () => void;
  onDownload: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
}) {
  const marks = request.snapshotList.reduce((n, s) => n + s.marks.length, 0);
  const [title, setTitle] = useState(request.title);
  return (
    <div className="ui-request-head">
      <input
        className="ui-title"
        value={title}
        maxLength={200}
        placeholder="Untitled request"
        aria-label="Request title"
        onChange={(e) => setTitle(e.target.value)}
        onBlur={() => title.trim() !== request.title && onRename(title.trim())}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      />
      <span className="muted">
        {request.frontend} · on {request.branch}
      </span>
      <button type="button" className="connect" onClick={onMatch} disabled={matching || !marks} title="Find where each mark is in the code (M)">
        {matching ? 'Matching…' : 'Match to code'}
      </button>
      <button type="button" className="connect" onClick={onInterpret} disabled={interpreting || matching || !marks} title="Say what each mark asks, for you to check">
        {interpreting ? 'Interpreting…' : request.interpretation ? 'Interpret again' : 'Interpret'}
      </button>
      <button type="button" className="link" onClick={onCopy} disabled={!marks} title="The request as one prompt, for any agent">
        Copy prompt
      </button>
      <button type="button" className="link" onClick={onDownload} disabled={!marks} title="The prompt and the images with the marks drawn on, as a .zip">
        Download for an agent
      </button>
      <button type="button" className="link danger" onClick={onDelete}>
        Delete request
      </button>
    </div>
  );
}

function SnapshotEditor({
  snapshot,
  mode,
  tool,
  selected,
  busy,
  onMode,
  onTool,
  onSelect,
  onCreate,
  onSave,
  onRemove,
  onOpenFile,
  interpretation,
  snapshotNumber,
  onCorrect,
  onDeleteSnapshot,
}: {
  snapshot: UiSnapshot;
  mode: AnnotateMode;
  tool: DrawTool;
  selected: string | null;
  busy: boolean;
  onMode: (mode: AnnotateMode) => void;
  onTool: (tool: DrawTool) => void;
  onSelect: (id: string | null) => void;
  onCreate: (mark: { geometry: MarkGeometry; element?: number }) => void;
  onSave: (mark: UiMark, patch: { note?: string; geometry?: MarkGeometry }) => void;
  onRemove: (mark: UiMark) => void;
  onOpenFile: (path: string, line?: number) => void;
  interpretation: UiRequest['interpretation'];
  snapshotNumber: number;
  onCorrect: (item: InterpretationItem) => void;
  onDeleteSnapshot: () => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const annotator = useRef<Annotator | null>(null);
  const [expired, setExpired] = useState(false);
  const rendered = useMemo(() => snapshot.marks.map((m, i) => ({ id: m.id, number: i + 1, geometry: m.geometry })), [snapshot.marks]);
  const elementScale = snapshot.viewport ? snapshot.width / snapshot.viewport.width : 1;

  // The latest callbacks, so the annotator — made once per snapshot — always calls the current ones.
  const latest = useRef({ onCreate, onSelect });
  latest.current = { onCreate, onSelect };

  useEffect(() => {
    if (!host.current) return;
    annotator.current = createAnnotator(host.current, {
      width: snapshot.width,
      height: snapshot.height,
      mode,
      tool,
      marks: rendered,
      selected,
      elements: snapshot.elements,
      elementScale,
      onCreate: (m) => latest.current.onCreate(m),
      onSelect: (id) => latest.current.onSelect(id),
    });
    return () => {
      annotator.current?.destroy();
      annotator.current = null;
    };
    // Made once per snapshot; everything else is an update below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot.id]);

  useEffect(() => {
    annotator.current?.update({ mode, tool, marks: rendered, selected });
  }, [mode, tool, rendered, selected]);

  return (
    <div className="ui-editor">
      <div className="ui-toolbar" role="toolbar" aria-label="Marking">
        <div className="ui-segmented" role="radiogroup" aria-label="Mode">
          {(['view', 'annotate', 'draw'] as const).map((m, i) => (
            <button key={m} type="button" role="radio" aria-checked={mode === m} className={mode === m ? 'on' : ''} onClick={() => onMode(m)} title={`${MODE_NAMES[m]} (${i + 1})`}>
              {MODE_NAMES[m]}
            </button>
          ))}
        </div>
        {mode === 'draw' ? (
          <div className="ui-segmented" role="radiogroup" aria-label="Tool">
            {DRAW_TOOLS.map((t) => (
              <button key={t} type="button" role="radio" aria-checked={tool === t} className={tool === t ? 'on' : ''} onClick={() => onTool(t)}>
                {TOOL_NAMES[t]}
              </button>
            ))}
          </div>
        ) : null}
        <span className="muted ui-hint">
          {mode === 'view'
            ? 'Tap a number to select its note.'
            : mode === 'annotate'
              ? 'Tap to pin a note; drag to mark a region.'
              : tool === 'text'
                ? 'Tap where the text goes.'
                : `Drag to draw ${tool === 'pen' ? 'freehand' : `a${tool === 'arrow' ? 'n arrow' : ' box'}`}.`}
        </span>
        <a className="link ui-snapshot-url" href={snapshot.url} target="_blank" rel="noopener noreferrer" title={snapshot.url}>
          {hostOf(snapshot.url)}
          {pathOf(snapshot.url)}
        </a>
        <button type="button" className="link danger" onClick={onDeleteSnapshot} disabled={busy}>
          Delete snapshot
        </button>
      </div>

      <RouteLine route={snapshot.route} matched={snapshot.marks.some((m) => m.matchedAt)} onOpenFile={onOpenFile} />
      <div className="ui-work">
      <div className="ui-stage">
        {expired ? (
          <p className="gate-error">This snapshot&rsquo;s image is no longer kept (images expire after 90 days). Its notes are below.</p>
        ) : (
          <div
            ref={host}
            className="ui-canvas"
            style={{ aspectRatio: `${snapshot.width} / ${snapshot.height}`, ['--aspect' as string]: snapshot.width / snapshot.height, maxWidth: `${snapshot.width}px` }}
          >
            <img src={snapshot.image} alt="The snapshot being marked up" draggable={false} onError={() => setExpired(true)} />
          </div>
        )}
      </div>

      <ol className="ui-marks" aria-label="Marks">
        {snapshot.marks.length === 0 ? <li className="muted">No marks yet.</li> : null}
        {snapshot.marks.map((m, i) => (
          <MarkRow
            key={m.id}
            mark={m}
            number={i + 1}
            selected={m.id === selected}
            element={m.element !== null ? snapshot.elements?.[m.element] : undefined}
            onSelect={() => onSelect(m.id)}
            onSave={(patch) => onSave(m, patch)}
            onRemove={() => onRemove(m)}
            onOpenFile={onOpenFile}
            item={interpretation?.items.find((it) => it.snapshot === snapshotNumber && it.mark === i + 1)}
            changedSince={!!interpretation && m.updatedAt > interpretation.createdAt}
            onCorrect={onCorrect}
          />
        ))}
      </ol>
      </div>
    </div>
  );
}

const pathOf = (url: string): string => {
  try {
    const u = new URL(url);
    return u.pathname === '/' ? '' : u.pathname;
  } catch {
    return '';
  }
};

function MarkRow({
  mark,
  number,
  selected,
  element,
  onSelect,
  onSave,
  onRemove,
  onOpenFile,
  item,
  changedSince,
  onCorrect,
}: {
  mark: UiMark;
  number: number;
  selected: boolean;
  element: { tag: string; text?: string | undefined } | undefined;
  onSelect: () => void;
  onSave: (patch: { note?: string; geometry?: MarkGeometry }) => void;
  onRemove: () => void;
  onOpenFile: (path: string, line?: number) => void;
  item: InterpretationItem | undefined;
  changedSince: boolean;
  onCorrect: (item: InterpretationItem) => void;
}) {
  const [note, setNote] = useState(mark.note);
  const [label, setLabel] = useState(mark.geometry.kind === 'stroke' ? (mark.geometry.label ?? '') : '');
  const field = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!selected || mark.note || !field.current) return;
    // Focus without scrolling the page: the snapshot stays where the engineer is looking,
    // and only the list of notes moves to show this one.
    field.current.focus({ preventScroll: true });
    const list = field.current.closest('.ui-marks');
    const row = field.current.closest('.ui-mark');
    if (list && row && list.scrollHeight > list.clientHeight) {
      list.scrollTop = (row as HTMLElement).offsetTop - (list as HTMLElement).offsetTop - 8;
    }
  }, [selected, mark.note]);

  const isText = mark.geometry.kind === 'stroke' && mark.geometry.tool === 'text';
  return (
    <li className={selected ? 'ui-mark selected' : 'ui-mark'} onClick={onSelect}>
      <span className="ui-mark-number" aria-hidden="true">
        {number}
      </span>
      <div className="ui-mark-body">
        <span className="ui-mark-kind">
          {describeMark(mark.geometry)}
          {element ? <span className="muted"> on {element.tag}{element.text ? ` “${element.text.slice(0, 40)}”` : ''}</span> : null}
        </span>
        {isText ? (
          <input
            type="text"
            className="ui-mark-label"
            value={label}
            maxLength={200}
            placeholder="What the drawing says"
            aria-label={`Text of mark ${number}`}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => {
              if (mark.geometry.kind === 'stroke' && label !== (mark.geometry.label ?? '')) onSave({ geometry: { ...mark.geometry, label } });
            }}
          />
        ) : null}
        <textarea
          ref={field}
          value={note}
          rows={2}
          maxLength={4000}
          placeholder={mark.geometry.kind === 'pin' ? 'What should change here?' : 'What does this mean?'}
          aria-label={`Note for mark ${number}`}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => note !== mark.note && onSave({ note })}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) (e.target as HTMLTextAreaElement).blur();
          }}
        />
        {mark.matches ? (
          <Matches match={mark.matches} stale={!!mark.matchedAt && mark.updatedAt > mark.matchedAt} onOpenFile={onOpenFile} />
        ) : null}
        {item ? <Instruction key={`${item.snapshot}:${item.mark}`} item={item} changedSince={changedSince} onOpenFile={onOpenFile} onCorrect={onCorrect} /> : null}
      </div>
      <button
        type="button"
        className="link danger"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        aria-label={`Remove mark ${number}`}
      >
        Remove
      </button>
    </li>
  );
}

/** A file path short enough to read beside a note; the whole path is its title. */
const shortPath = (path: string): string => path.split('/').slice(-2).join('/');

function FileLink({ path, line, onOpenFile }: { path: string; line?: number | undefined; onOpenFile: (path: string, line?: number) => void }) {
  return (
    <button type="button" className="ui-file" title={line ? `${path}:${line}` : path} onClick={(e) => {
      e.stopPropagation();
      onOpenFile(path, line);
    }}>
      {shortPath(path)}
      {line ? `:${line}` : ''}
    </button>
  );
}

/** The page a snapshot shows, as its frontend's code declares the route. */
function RouteLine({ route, matched, onOpenFile }: { route: RouteMatch | null | undefined; matched: boolean; onOpenFile: (path: string, line?: number) => void }) {
  if (!matched) return null;
  if (!route) return <p className="ui-route muted">Civil found no route in the code for this address.</p>;
  return (
    <p className="ui-route">
      <span className="muted">Page</span> <code>{route.pattern}</code>
      {route.renders ? (
        <>
          {' '}
          <span className="muted">is</span> <FileLink path={route.renders} onOpenFile={onOpenFile} />
        </>
      ) : null}
      <span className="muted"> · declared at </span>
      <FileLink path={route.file} line={route.line} onOpenFile={onOpenFile} />
    </p>
  );
}

/** Where a mark is in the code: evidence Civil read, or a guess that says it is one. */
function Matches({ match, stale, onOpenFile }: { match: MarkMatch; stale: boolean; onOpenFile: (path: string, line?: number) => void }) {
  return (
    <div className="ui-matches">
      {match.what ? <span className="muted">Read as {match.what}</span> : null}
      {match.evidence.length ? (
        <ul>
          {match.evidence.map((e) => (
            <li key={`${e.file}:${e.line}`}>
              <FileLink path={e.file} line={e.line} onOpenFile={onOpenFile} /> <span className="ui-quote">“{e.text}”</span>
              {e.where === 'frontend' ? <span className="muted"> · not on this page’s files</span> : null}
            </li>
          ))}
        </ul>
      ) : match.guess ? (
        <p className="ui-guess">
          <span className="ui-guess-label">Guess</span> {match.guess.why}
          {match.guess.file ? (
            <>
              {' — '}
              <FileLink path={match.guess.file} onOpenFile={onOpenFile} />
            </>
          ) : null}
        </p>
      ) : null}
      {stale ? <span className="ui-stale">Changed since it was matched</span> : null}
    </div>
  );
}

/**
 * What Civil understood a mark to ask (docs/ui-tab.md §7), for the engineer to correct
 * in place: what they write here is what an export or an edit uses.
 */
function Instruction({
  item,
  changedSince,
  onOpenFile,
  onCorrect,
}: {
  item: InterpretationItem;
  changedSince: boolean;
  onOpenFile: (path: string, line?: number) => void;
  onCorrect: (item: InterpretationItem) => void;
}) {
  const [draft, setDraft] = useState(item);
  const save = (next: InterpretationItem) => {
    const clean = { ...next, understood: next.understood.trim() || item.understood, change: next.change.trim() };
    if (JSON.stringify(clean) !== JSON.stringify(item)) onCorrect(clean);
  };
  const field = (key: 'understood' | 'change' | 'question', label: string) => (
    <label className="ui-instruction-field">
      <span>{label}</span>
      <textarea
        rows={2}
        maxLength={1200}
        value={draft[key] ?? ''}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        onBlur={() => save(key === 'question' && !draft.question?.trim() ? (({ question: _q, ...rest }) => rest)(draft) : draft)}
      />
    </label>
  );
  return (
    <div className="ui-instruction">
      <span className="ui-instruction-head">
        Civil understood{changedSince ? <span className="ui-stale"> · the mark changed since</span> : null}
      </span>
      {field('understood', 'Asks')}
      {draft.where.length ? (
        <p className="ui-instruction-where">
          <span>Where</span>{' '}
          {draft.where.map((w, i) => (
            <span key={`${w.file}:${w.line ?? ''}`}>
              {i ? ', ' : ''}
              <FileLink path={w.file} line={w.line} onOpenFile={onOpenFile} />
            </span>
          ))}
        </p>
      ) : null}
      {field('change', 'Change')}
      {item.question !== undefined ? field('question', 'Question for you') : null}
    </div>
  );
}

function LinkForm({
  projectId,
  frontend,
  onDone,
}: {
  projectId: string;
  frontend: string;
  onDone: (effect: string, failed: boolean, shared: boolean, name: string) => void;
}) {
  const [name, setName] = useState('staging');
  const [url, setUrl] = useState('');
  const [shared, setShared] = useState(true);
  const [busy, setBusy] = useState(false);
  const local = /^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])/i.test(url);
  useEffect(() => {
    // A localhost address is one engineer's machine: never everyone's link.
    if (local) setShared(false);
  }, [local]);

  return (
    <form
      className="ui-link-form"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        const work = shared
          ? applyUiOps(projectId, [{ op: 'setLink', frontend, name, url }])
          : setPersonalLink(projectId, { frontend, name, url });
        void work
          .then((r) => onDone(r.effect, false, shared, name))
          .catch((error: unknown) => onDone((error as Error).message, true, shared, name))
          .finally(() => setBusy(false));
      }}
    >
      <label className="ui-field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value.toLowerCase())} pattern="[a-z][a-z0-9_\-]{0,31}" required aria-label="Link name" />
      </label>
      <label className="ui-field ui-grow">
        <span>Address</span>
        <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://staging.example.com" required aria-label="Link address" />
      </label>
      <fieldset className="ui-segmented" aria-label="Who sees it">
        <button type="button" className={shared ? 'on' : ''} aria-pressed={shared} onClick={() => setShared(true)} disabled={local}>
          Everyone (civil/ui.yaml)
        </button>
        <button type="button" className={shared ? '' : 'on'} aria-pressed={!shared} onClick={() => setShared(false)}>
          Just me
        </button>
      </fieldset>
      <button type="submit" className="connect" disabled={busy}>
        Save link
      </button>
    </form>
  );
}

function LinkRemove({
  projectId,
  frontend,
  link,
  onDone,
}: {
  projectId: string;
  frontend: string;
  link: { name: string; url: string; shared: boolean };
  onDone: (effect: string, failed: boolean) => void;
}) {
  return (
    <button
      type="button"
      className="link"
      onClick={() => {
        if (!window.confirm(`Remove the link “${link.name}”${link.shared ? ' from civil/ui.yaml (a pending change)' : ''}?`)) return;
        const work = link.shared
          ? applyUiOps(projectId, [{ op: 'removeLink', frontend, name: link.name }])
          : deletePersonalLink(projectId, { frontend, name: link.name });
        void work.then((r) => onDone(r.effect, false)).catch((error: unknown) => onDone((error as Error).message, true));
      }}
    >
      Remove link
    </button>
  );
}
